/**
 * Auth utilities - real Cognito integration using amazon-cognito-identity-js.
 */

import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { COGNITO_USER_POOL_ID, COGNITO_CLIENT_ID } from '@/api/config';

export interface AuthSession {
  userId: string;
  email: string;
  token: string;
  refreshToken: string;
  expiresAt: number;
}

/** Lazy-initialized user pool singleton. Only created when Cognito is configured. */
let userPool: CognitoUserPool | null = null;

export function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID) {
      throw new Error('Cognito is not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.');
    }
    userPool = new CognitoUserPool({
      UserPoolId: COGNITO_USER_POOL_ID,
      ClientId: COGNITO_CLIENT_ID,
    });
  }
  return userPool;
}

function createCognitoUser(email: string): CognitoUser {
  return new CognitoUser({
    Username: email,
    Pool: getUserPool(),
  });
}

function sessionToAuthSession(session: CognitoUserSession): AuthSession {
  const idToken = session.getIdToken();
  const payload = idToken.decodePayload();
  return {
    userId: payload['sub'] as string,
    email: payload['email'] as string,
    token: idToken.getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    expiresAt: idToken.getExpiration() * 1000,
  };
}

/**
 * Register a new account with Cognito.
 * The user will need to confirm their email with a verification code.
 */
export function signUp(
  email: string,
  password: string,
  name: string,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const attributes = [
      new CognitoUserAttribute({ Name: 'email', Value: email }),
      new CognitoUserAttribute({ Name: 'name', Value: name }),
    ];

    getUserPool().signUp(email, password, attributes, [], (err, _result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

/**
 * Confirm sign-up with email verification code.
 */
export function confirmSignUp(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = createCognitoUser(email);
    cognitoUser.confirmRegistration(code, true, (err, _result) => {
      if (err) {
        reject(err);
        return;
      }
      resolve();
    });
  });
}

/** Result of a sign-in attempt — either a session, TOTP MFA, or WebAuthn challenge. */
export type SignInResult =
  | { type: 'session'; session: AuthSession }
  | { type: 'mfa'; cognitoUser: CognitoUser }
  | { type: 'webauthn'; cognitoUser: CognitoUser; challengeOptions: string };

/**
 * Sign in with email and password.
 * If MFA is enabled, returns an MFA or WebAuthn challenge instead of a session.
 */
export function signIn(
  email: string,
  password: string,
): Promise<SignInResult> {
  return new Promise((resolve, reject) => {
    const cognitoUser = createCognitoUser(email);
    const authDetails = new AuthenticationDetails({
      Username: email,
      Password: password,
    });

    cognitoUser.authenticateUser(authDetails, {
      onSuccess: (session) => {
        resolve({ type: 'session', session: sessionToAuthSession(session) });
      },
      onFailure: (err) => {
        reject(err);
      },
      totpRequired: () => {
        resolve({ type: 'mfa', cognitoUser });
      },
      customChallenge: (challengeParameters) => {
        const webauthnOptions = challengeParameters?.webauthnOptions as string | undefined;
        if (webauthnOptions) {
          resolve({ type: 'webauthn', cognitoUser, challengeOptions: webauthnOptions });
        } else {
          reject(new Error('Unknown custom challenge'));
        }
      },
    });
  });
}

/**
 * Sign in with a security key via our backend.
 * The backend handles WebAuthn verification and Cognito token issuance.
 */
export async function signInWithWebAuthn(
  email: string,
  password: string,
): Promise<AuthSession> {
  const { API_BASE_URL } = await import('@/api/config');
  const { startAuthentication } = await import('@simplewebauthn/browser');

  // 1. Get authentication options from our backend (unauthenticated)
  const optionsRes = await fetch(`${API_BASE_URL}/auth/webauthn/options`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!optionsRes.ok) {
    const err = await optionsRes.json().catch(() => ({ error: 'Failed to get security key options' })) as { error: string };
    throw new Error(err.error);
  }
  const options = await optionsRes.json() as Record<string, unknown>;

  // 2. Browser WebAuthn ceremony
  const assertion = await startAuthentication({
    optionsJSON: options as unknown as Parameters<typeof startAuthentication>[0]['optionsJSON'],
  });

  // 3. Encrypt password with the server's one-time RSA public key
  const passwordPublicKey = (options as { passwordPublicKey?: string }).passwordPublicKey;
  let encryptedPassword: string;
  if (passwordPublicKey) {
    const pemContents = passwordPublicKey
      .replace(/-----BEGIN PUBLIC KEY-----/, '')
      .replace(/-----END PUBLIC KEY-----/, '')
      .replace(/\s/g, '');
    const keyBuffer = Uint8Array.from(atob(pemContents), (c) => c.charCodeAt(0));
    const importedKey = await crypto.subtle.importKey(
      'spki',
      keyBuffer,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt'],
    );
    const encrypted = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      importedKey,
      new TextEncoder().encode(password),
    );
    encryptedPassword = btoa(String.fromCharCode(...new Uint8Array(encrypted)));
  } else {
    // Fallback: shouldn't happen, but don't block login
    encryptedPassword = btoa(password);
  }

  // 4. Send assertion + encrypted password to backend
  const verifyRes = await fetch(`${API_BASE_URL}/auth/webauthn/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, encryptedPassword, assertion }),
  });
  if (!verifyRes.ok) {
    const err = await verifyRes.json().catch(() => ({ error: 'Security key verification failed' })) as { error: string };
    throw new Error(err.error);
  }
  const tokens = await verifyRes.json() as {
    idToken: string;
    accessToken: string;
    refreshToken: string;
  };

  // 5. Store the session in the Cognito SDK so token refresh works
  const cognitoUser = createCognitoUser(email);
  const {
    CognitoIdToken,
    CognitoAccessToken,
    CognitoRefreshToken,
  } = await import('amazon-cognito-identity-js');
  const session = new CognitoUserSession({
    IdToken: new CognitoIdToken({ IdToken: tokens.idToken }),
    AccessToken: new CognitoAccessToken({ AccessToken: tokens.accessToken }),
    RefreshToken: new CognitoRefreshToken({ RefreshToken: tokens.refreshToken }),
  });
  cognitoUser.setSignInUserSession(session);

  return sessionToAuthSession(session);
}

/**
 * Complete WebAuthn sign-in by submitting the authenticator assertion.
 */
export function respondToWebAuthnChallenge(
  cognitoUser: CognitoUser,
  assertionResponseJson: string,
): Promise<AuthSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendCustomChallengeAnswer(
      assertionResponseJson,
      {
        onSuccess: (session) => resolve(sessionToAuthSession(session)),
        onFailure: (err) => reject(err),
        customChallenge: () => reject(new Error('WebAuthn verification failed')),
      },
    );
  });
}

/**
 * Complete MFA sign-in by providing the TOTP code.
 */
export function respondToMfaChallenge(
  cognitoUser: CognitoUser,
  totpCode: string,
): Promise<AuthSession> {
  return new Promise((resolve, reject) => {
    cognitoUser.sendMFACode(
      totpCode,
      {
        onSuccess: (session) => {
          resolve(sessionToAuthSession(session));
        },
        onFailure: (err) => {
          reject(err);
        },
      },
      'SOFTWARE_TOKEN_MFA',
    );
  });
}

/**
 * Associate a TOTP device with the current user.
 * Returns the secret code for QR code generation.
 */
export function setupTotp(): Promise<string> {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }
    cognitoUser.getSession((err: Error | null) => {
      if (err) { reject(err); return; }
      cognitoUser.associateSoftwareToken({
        associateSecretCode: (secretCode: string) => {
          resolve(secretCode);
        },
        onFailure: (err2: Error) => {
          reject(err2);
        },
      });
    });
  });
}

/**
 * Verify the TOTP setup by providing a code from the authenticator app.
 */
export function verifyTotp(totpCode: string, friendlyName: string = 'Authenticator'): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }
    cognitoUser.getSession((err: Error | null) => {
      if (err) { reject(err); return; }
      cognitoUser.verifySoftwareToken(totpCode, friendlyName, {
        onSuccess: () => {
          // Enable TOTP as the preferred MFA method
          cognitoUser.setUserMfaPreference(
            null, // SMS not used
            { PreferredMfa: true, Enabled: true }, // TOTP
            (mfaErr) => {
              if (mfaErr) { reject(mfaErr); return; }
              resolve();
            },
          );
        },
        onFailure: (err2: Error) => {
          reject(err2);
        },
      });
    });
  });
}

/**
 * Disable MFA for the current user.
 */
export function disableMfa(): Promise<void> {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }
    cognitoUser.getSession((err: Error | null) => {
      if (err) { reject(err); return; }
      cognitoUser.setUserMfaPreference(
        null,
        { PreferredMfa: false, Enabled: false },
        (mfaErr) => {
          if (mfaErr) { reject(mfaErr); return; }
          resolve();
        },
      );
    });
  });
}

/**
 * Check if the current user has MFA enabled.
 * Uses getUserData() which returns UserMFASettingList — reliable for TOTP.
 */
export function getMfaStatus(): Promise<{ enabled: boolean; preferred: string | null }> {
  return new Promise((resolve, reject) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      reject(new Error('No authenticated user'));
      return;
    }
    cognitoUser.getSession((err: Error | null) => {
      if (err) { reject(err); return; }
      cognitoUser.getUserData((dataErr, data) => {
        if (dataErr || !data) {
          // Fallback: assume not enabled if we can't read user data
          resolve({ enabled: false, preferred: null });
          return;
        }
        const mfaSettings = data.UserMFASettingList ?? [];
        const hasTotp = mfaSettings.includes('SOFTWARE_TOKEN_MFA');
        const preferred = data.PreferredMfaSetting ?? null;
        resolve({ enabled: hasTotp, preferred });
      });
    });
  });
}

/**
 * Sign out the current user and clear stored session data.
 */
export function signOut(): void {
  const cognitoUser = getUserPool().getCurrentUser();
  if (cognitoUser) {
    cognitoUser.signOut();
  }
}

/**
 * Get the current valid session, or null if none exists.
 * Handles token refresh automatically via the Cognito SDK.
 */
export function getCurrentSession(): Promise<AuthSession | null> {
  if (!COGNITO_USER_POOL_ID || !COGNITO_CLIENT_ID) {
    return Promise.resolve(null);
  }
  return new Promise((resolve) => {
    const cognitoUser = getUserPool().getCurrentUser();
    if (!cognitoUser) {
      resolve(null);
      return;
    }

    cognitoUser.getSession(
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session || !session.isValid()) {
          resolve(null);
          return;
        }
        resolve(sessionToAuthSession(session));
      },
    );
  });
}

/**
 * Check whether the user has a valid (non-expired) session.
 */
export async function isAuthenticated(): Promise<boolean> {
  const session = await getCurrentSession();
  return session !== null;
}

/**
 * Get the current JWT ID token string for API calls.
 * Returns null if no valid session exists.
 */
export async function getIdToken(): Promise<string | null> {
  const session = await getCurrentSession();
  return session?.token ?? null;
}
